import { useEffect, useRef } from "react";
import { toast } from "sonner";

const FIRST_REMINDER_MS = 10 * 60 * 1000;
const REPEAT_REMINDER_MS = 15 * 60 * 1000;

async function playPauseAlert() {
  try {
    for (let i = 0; i < 3; i += 1) {
      const audio = new Audio("/workshop-pause-alert.wav");
      audio.volume = 1;
      await audio.play();
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
      });
      if (i < 2) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    return true;
  } catch {
    return false;
  }
}

async function showSystemNotification(job, pausedMinutes) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return false;
  }

  const label = job.title || job.customer_name || "Auftrag";
  const options = {
    body: `${label} – Timer seit ${pausedMinutes} Min. pausiert.`,
    icon: "/logo192.svg",
    badge: "/logo192.svg",
    tag: `stellar-workshop-pause-${job.id}`,
    renotify: true,
    requireInteraction: true,
    data: { url: `/werkstatt?job=${job.id}` },
  };

  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification("Werkstatt-Timer pausiert", options);
    } else {
      new Notification("Werkstatt-Timer pausiert", options);
    }
    return true;
  } catch {
    return false;
  }
}

export function useWorkshopPauseReminder(candidates = []) {
  const lastReminderRef = useRef({});

  useEffect(() => {
    const check = async () => {
      const pausedJobs = candidates.filter(
        (job) => job.timer_state === "paused" && job.paused_since
      );

      const pausedIds = new Set(pausedJobs.map((job) => job.id));

      for (const id of Object.keys(lastReminderRef.current)) {
        if (!pausedIds.has(id)) {
          delete lastReminderRef.current[id];
        }
      }

      const now = Date.now();

      for (const job of pausedJobs) {
        const pausedAt = new Date(job.paused_since).getTime();
        if (!Number.isFinite(pausedAt)) continue;

        const pausedFor = now - pausedAt;
        if (pausedFor < FIRST_REMINDER_MS) continue;

        const lastReminder = lastReminderRef.current[job.id] || 0;
        if (lastReminder && now - lastReminder < REPEAT_REMINDER_MS) continue;

        lastReminderRef.current[job.id] = now;

        const pausedMinutes = Math.max(1, Math.floor(pausedFor / 60000));
        await playPauseAlert();
        const notified = await showSystemNotification(job, pausedMinutes);

        if (!notified && document.visibilityState === "visible") {
          const label = job.title || job.customer_name || "Auftrag";
          toast.warning(`${label}: Timer seit ${pausedMinutes} Min. pausiert.`);
        }
      }
    };

    check();
    const interval = window.setInterval(check, 60 * 1000);

    return () => window.clearInterval(interval);
  }, [candidates]);
}
